export interface PasteFile {
  path: string;
  content: string;
}

export interface VersionInfo {
  id: string;
  createdAt: number;
  title: string;
  fileCount: number;
}

export type ChatRole = "user" | "assistant" | "tool";

export interface ChatMessage {
  id: string;
  seq: number;
  role: ChatRole;
  content: string;
  meta: unknown;
  createdAt: number;
}

export interface ChatPage {
  threadId: string;
  messages: ChatMessage[];
  hasMore: boolean;
  totalBytes: number;
  warn: boolean;
}

export interface ChatAppendResult {
  messages: ChatMessage[];
  totalBytes: number;
  warn: boolean;
}

export interface PasteResponse {
  id: string;
  slug: string;
  title: string;
  updatedAt: number;
  files: PasteFile[];
  isOwner: boolean;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  // Content-Type ставим только когда реально есть тело: иначе Fastify
  // отвергает пустой JSON-body (FST_ERR_CTP_EMPTY_JSON_BODY).
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined) };
  if (init?.body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(url, { credentials: "include", ...init, headers });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as T;
}

export interface SessionChallenge {
  id: string;
  prefix: string;
  difficulty: number;
}

export const api = {
  createChallenge: () => req<SessionChallenge>("/api/session/challenge", { method: "POST" }),

  establishSession: (challenge_id: string, nonce: string) =>
    req<{ ok: true }>("/api/session", {
      method: "POST",
      body: JSON.stringify({ challenge_id, nonce }),
    }),

  me: () => req<{ session_id: string }>("/api/me"),

  createPaste: (title: string, files: PasteFile[]) =>
    req<{ slug: string }>("/api/pastes", {
      method: "POST",
      body: JSON.stringify({ title, files }),
    }),

  getPaste: (slug: string) => req<PasteResponse>(`/api/pastes/${encodeURIComponent(slug)}`),

  updatePaste: (slug: string, patch: { title?: string; files?: PasteFile[] }) =>
    req<{ updatedAt: number }>(`/api/pastes/${encodeURIComponent(slug)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  listVersions: (slug: string) =>
    req<{ versions: VersionInfo[] }>(`/api/pastes/${encodeURIComponent(slug)}/versions`),

  saveVersion: (slug: string) =>
    req<VersionInfo>(`/api/pastes/${encodeURIComponent(slug)}/versions`, { method: "POST" }),

  aiConfig: () => req<{ enabled: boolean; model: string }>("/api/ai/config"),

  aiPing: () =>
    req<{
      ok: boolean;
      stage: string;
      status?: number;
      message?: string;
      model?: string;
    }>("/api/ai/ping"),

  listChat: (slug: string, opts: { limit?: number; before?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.limit) q.set("limit", String(opts.limit));
    if (opts.before) q.set("before", String(opts.before));
    const qs = q.toString();
    return req<ChatPage>(`/api/pastes/${encodeURIComponent(slug)}/chat${qs ? `?${qs}` : ""}`);
  },

  appendChat: (slug: string, messages: Array<{ role: ChatRole; content: string; meta?: unknown }>) =>
    req<ChatAppendResult>(`/api/pastes/${encodeURIComponent(slug)}/chat`, {
      method: "POST",
      body: JSON.stringify({ messages }),
    }),

  clearChat: (slug: string) =>
    req<{ threadId: string }>(`/api/pastes/${encodeURIComponent(slug)}/chat`, { method: "DELETE" }),

  versionFiles: (slug: string, versionId: string) =>
    req<{ files: PasteFile[] }>(
      `/api/pastes/${encodeURIComponent(slug)}/versions/${encodeURIComponent(versionId)}/files`,
    ),

  forkPaste: (slug: string, title?: string) =>
    req<{ slug: string }>(`/api/pastes/${encodeURIComponent(slug)}/fork`, {
      method: "POST",
      body: JSON.stringify({ title }),
    }),

  /**
   * Кладёт собранный HTML превью в память сервера (не в БД) и возвращает
   * токен черновика. Нужен, чтобы iframe грузил живое содержимое по
   * настоящему URL, а не через about:srcdoc.
   */
  putDraft: (html: string, token: string | null) =>
    req<{ token: string }>("/api/drafts", {
      method: "POST",
      body: JSON.stringify(token ? { html, token } : { html }),
    }),

  restoreVersion: (slug: string, versionId: string) =>
    req<{ updatedAt: number }>(
      `/api/pastes/${encodeURIComponent(slug)}/versions/${encodeURIComponent(versionId)}/restore`,
      { method: "POST" },
    ),
};